import React, { useEffect, useRef } from 'react';
import styles from './StateHierarchy.module.css';

interface StateHierarchyComponent {
  id: string;
  name: string;
  type: string;
  metadata?: Record<string, any>;
}

interface StateHierarchyData {
  current_path: string;
  path_components: StateHierarchyComponent[];
  current_state_id: string;
}

interface StateHierarchyProps {
  stateHierarchy: StateHierarchyData | null;
}

const StateHierarchy: React.FC<StateHierarchyProps> = ({ stateHierarchy }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // If no state hierarchy data, show default Introduction state
  const path_components = stateHierarchy?.path_components && stateHierarchy.path_components.length > 0 
    ? stateHierarchy.path_components 
    : [
        {
          id: 'default-introduction',
          name: 'Introduction',
          type: 'phase',
          metadata: {}
        }
      ];

  // Scroll to bottom whenever state hierarchy updates
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [stateHierarchy]);

  return (
    <div className={styles.stateHierarchyContainer}>
      <div className={styles.stateHierarchyHeader}>
        <h3 className={styles.stateHierarchyTitle}>Stage</h3>
      </div>
      
      <div className={styles.stateHierarchyContent} ref={scrollContainerRef}>
        {path_components.map((component, index) => (
          <div key={component.id} className={styles.stateItem}>
            <div className={styles.stateNode}>
              {/* <div className={`${styles.stateIndicator} ${styles[component.type]}`}>
                {getStateIcon(component.type)}
              </div> */}
              <div className={styles.stateInfo}>
                <span className={styles.stateName}>{component.name}</span>
                <span className={styles.stateType}>{formatStateType(component.type)}</span>
              </div>
            </div>
            
            {index < path_components.length - 1 && (
              <div className={styles.stateArrow}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const getStateIcon = (type: string): React.ReactNode => {
  switch (type) {
    case 'root':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        </svg>
      );
    case 'phase':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="9" cy="9" r="2"></circle>
          <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
        </svg>
      );
    case 'section':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14,2 14,8 20,8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10,9 9,9 8,9"></polyline>
        </svg>
      );
    case 'dynamic':
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
        </svg>
      );
    default:
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
        </svg>
      );
  }
};

const formatStateType = (type: string): string => {
  switch (type) {
    case 'root':
      return 'Start';
    case 'phase':
      return 'Phase';
    case 'section':
      return 'Section';
    case 'dynamic':
      return 'Focus';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

export default StateHierarchy;
